import { Row, Typography } from '@vivaldis/antd-ui'
import { useMemo } from 'react'
import { Information } from '@vivaldis/web-ui'
import { useTranslation } from 'react-i18next'
import { Divider } from '../../../Divider'
import {
  LATE_SHIFT_BONUS_PREMIUM,
  MEAL_VOUCHERS_PREMIUM,
  MORNING_SHIFT_BONUS_PREMIUM,
  NIGHT_SHIFT_BONUS_PREMIUM,
  usePremiumTypeOptions
} from '../../../Premiums'
import { usePremiumUnitsTranslation } from '../../../Premiums'
import type { Contract } from '../../typings/Contract'

interface Props {
  contract: Contract
}

export const Premiums = ({ contract }: Props) => {
  const [t] = useTranslation('web_ui')

  const showPremiums = useMemo(() => {
    // this field is available only for SU
    return Object.hasOwn(contract, 'contractPremiums')
  }, [contract])

  const premiums = usePremiumTypeOptions(
    contract.contractPremiums?.map(
      positionPremium => positionPremium.premium
    ) || []
  )

  const translateUnit = usePremiumUnitsTranslation()

  return showPremiums ? (
    <>
      <Divider sectionSeparator={true} />
      <Typography.Title level={5} marginBottom={16}>
        {t('view_contract_screen_content.premiums')}
      </Typography.Title>
      <Information>
        <Information.InformationField
          label={t('view_contract_screen_content.premiums')}
        >
          {premiums?.length
            ? premiums?.map(premium => (
                <Row key={premium.label} justify="end">
                  {premium.label}
                </Row>
              ))
            : t('no', { ns: 'translation' })}
        </Information.InformationField>

        {contract.contractPremiums?.map(contractPremium => {
          switch (contractPremium.premium.name) {
            case LATE_SHIFT_BONUS_PREMIUM: {
              return (
                <Information.InformationField
                  key={contractPremium.premium.id}
                  label={t('premiums.type_options.late_shift_bonus')}
                >
                  {contractPremium.value ?? 0}{' '}
                  {translateUnit(contractPremium.unit)}
                </Information.InformationField>
              )
            }
            case NIGHT_SHIFT_BONUS_PREMIUM: {
              return (
                <Information.InformationField
                  key={contractPremium.premium.id}
                  label={t('premiums.type_options.night_shift_bonus')}
                >
                  {contractPremium.value ?? 0}{' '}
                  {translateUnit(contractPremium.unit)}
                </Information.InformationField>
              )
            }

            case MORNING_SHIFT_BONUS_PREMIUM:
              return (
                <Information.InformationField
                  key={contractPremium.premium.id}
                  label={t('premiums.type_options.morning_shift_bonus')}
                >
                  {contractPremium.value ?? 0}{' '}
                  {translateUnit(contractPremium.unit)}
                </Information.InformationField>
              )
            case MEAL_VOUCHERS_PREMIUM:
              return (
                <Information.InformationField
                  key={contractPremium.premium.id}
                  label={t('premiums.type_options.meal_vouchers')}
                >
                  <>
                    {contractPremium.value ?? 0}{' '}
                    {translateUnit(contractPremium.unit)}
                    {' / '}
                    {contractPremium.contributionValue ?? 0}{' '}
                    {translateUnit(contractPremium.unit)}
                  </>
                </Information.InformationField>
              )
          }
          return null
        }) || null}
      </Information>
    </>
  ) : null
}
